<template>
  <div class="header-wrapper" :style="`--header-padding-left: ${headerPaddingLeft}px`">
    <div class="logo-wrapper">
      <icon-logo color="white" class="logo" />
    </div>
    <div class="actions-bar">
      <template v-if="branchNames?.length > 1">
        <div class="branches-dropdown-wrapper">
          <rbz-dropdown
            :items="branchNames"
            :value="selectedBranchId"
            @update:value="switchBranch($event)"
            :placeholder="t('header.chooseBranch')"
            classes="branch-selection ignore-fix-focus"
            data-qa="switch-branch-dropdown"
          />
        </div>
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="gray" />
        </div>
      </template>
      <rbz-dropdown-menu
        trigger-button-icon="IconUser"
        :trigger-button-icon-props="{ color: 'white' }"
        trigger-button-classes="ignore-fix-focus"
        ref="dropdownMenu"
      >
        <template #dropdownMenuContent>
          <div class="account-info" v-if="accountDetails">
            <div class="account-title">
              {{ t('header.account') }}
            </div>
            <div class="account-details">
              <div class="account-name ellipsis" v-b-tooltip="accountDetails['contact_name']">
                {{ accountDetails['contact_name'] }}
              </div>
              <div class="account-email ellipsis" v-b-tooltip="accountDetails?.email">
                {{ accountDetails?.email }}
              </div>
            </div>
          </div>
          <layout-divider-line color="light-gray" />
          <router-link
            v-if="accountButtonVisible"
            :to="accountPath"
            tabindex="-1"
            class="router-link"
          >
            <menu-list-item
              data-qa="account-details-button"
              :title="t('header.accountDetails')"
              @item-click="navigateToPage(accountPath)"
              class="account-details-button"
              classes="header-menu-button ignore-fix-focus"
            />
          </router-link>
          <router-link :to="supportPath" tabindex="-1" class="router-link">
            <menu-list-item
              data-qa="support-button"
              :title="t('header.support')"
              @item-click="navigateToPage(supportPath)"
              class="support-button"
              classes="header-menu-button ignore-fix-focus"
            />
          </router-link>
          <a target="_blank" tabindex="-1" :href="userGuidePath">
            <menu-list-item
              data-qa="user-guide-button"
              :title="t('header.userGuide')"
              @item-click="openUserGuide(supportPath)"
              class="user-guide-button"
              classes="header-menu-button ignore-fix-focus"
            />
          </a>
          <layout-divider-line color="light-gray" />
          <menu-list-item
            data-qa="logout-button"
            :title="t('header.logout')"
            @item-click="
              logout();
              dropdownMenu.closeMenu();
            "
            class="logout-button"
            classes="header-menu-button ignore-fix-focus"
          />
        </template>
      </rbz-dropdown-menu>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import _ from 'lodash';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { useUserStore } from '@/store/UserStore';
import { useBranchesStore } from '@/store/BranchesStore';
import { useSideMenuStore } from '@/store/SideMenuStore';
import { useRouter, useRoute } from 'vue-router';
import { Branch, UserDetails } from '@/types';
import { DOCS_URL } from '@/types/externalUrls';
import RbzDropdown from '@/components/RbzDropdown.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import IconLogo from 'ui-component-library/assets/icons/IconLogo.vue';
import RbzDropdownMenu from '@/components/RbzDropdownMenu.vue';
import { useI18n } from 'vue-i18n';
import { ENV, SIDE_MENU } from '@/constants';
import MenuListItem from 'ui-component-library/rbz/components/MenuListItem.vue';

const { t } = useI18n();

const userStore = useUserStore();
const branchesStore = useBranchesStore();
const sideMenuStore = useSideMenuStore();

const headerPaddingLeft = computed(() => {
  const isCollapsed = sideMenuStore?.isCollapsed ?? true;
  const menuWidth = isCollapsed ? SIDE_MENU.WIDTH_COLLAPSED : SIDE_MENU.WIDTH_EXPANDED;
  return menuWidth + SIDE_MENU.HEADER_GAP;
});

const router = useRouter();
const route = useRoute();
const branches = ref([]);
const accountDetails = ref<UserDetails>(null);

const accountButtonVisible = ref(
  userStore.checkAccessLevel(userStore.accessLevels.waapConfigEditor) &&
    (userStore.getCookieProfileProvider() === 'reblaze' || ENV.DEV)
);
const accountPath = computed(() => {
  return `/${selectedBranchId.value}/account`;
});
const supportPath = computed(() => {
  return `/${selectedBranchId.value}/support`;
});
const userGuidePath = computed(() => {
  return DOCS_URL;
});
const navigateToPage = (path: string) => {
  router.push(path);
  dropdownMenu.value.closeMenu();
};
const openUserGuide = () => {
  window.open(userGuidePath.value, '_blank');
  dropdownMenu.value.closeMenu();
};

const branchNames = computed((): string[][] => {
  return branches.value?.length
    ? _.sortBy(
        _.map(branches.value, (branch) => {
          return [branch.id, branch.id];
        })
      )
    : [];
});

const selectedBranchId = computed((): string => {
  return branchesStore.selectedBranchId;
});

const branchesCounter = computed((): number => {
  return branchesStore.branchesCounter;
});

watch(branchesCounter, async () => {
  branches.value = await branchesStore.getBranches();
});

const loadAccountDetails = async () => {
  const provider = userStore.getCookieProfileProvider();
  const accountId = userStore.getCookieAccountId();
  if (provider === 'reblaze') {
    if (accountId) {
      const url = `accounts/${accountId}`;
      const method = 'GET';
      const response = await RequestsUtils.sendRequest({
        methodName: method,
        url,
      });
      accountDetails.value = response?.data || { email: userStore.getCookieEmail() };
      accountDetails.value.id = accountId;
      userStore.subscribeToChanges(loadAccountDetails);
    }
  } else {
    accountDetails.value = {
      id: accountId,
      email: userStore.getCookieEmail(),
      contact_name: userStore.getCookieName(),
    };
  }
  userStore.updateUserIdentifier(accountDetails.value);
};

const switchBranch = (newBranchId: Branch['id']) => {
  if (route.name && newBranchId) {
    Utils.toast({
      message: t('header.branchChangedToast', { branch: newBranchId }),
      type: 'is-info',
    });
    branchesStore.setSelectedBranch(newBranchId);
    router.push({ name: route.name, params: { branch: newBranchId } });
  }
};

const logout = () => {
  userStore.logout();
};

const dropdownMenu = ref(null);

onMounted(() => {
  loadAccountDetails();
});

onBeforeMount(async () => {
  branches.value = await branchesStore.getBranches();
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

$dropdown-max-width: 240px;

.header-wrapper {
  background-color: map.get(colors.$colors, 'stratos');
  display: flex;
  height: 56px;
  justify-content: space-between;
  padding-left: var(--header-padding-left, 123px);
  padding-right: 32px;
  position: static;
  user-select: none;
  width: 100%;
}

.logo-wrapper {
  align-items: center;
  display: flex;
  height: 26px;
  margin-top: 18px;

  .logo {
    height: 32px;
    width: 139px;
  }
}

.actions-bar {
  align-items: center;
  display: flex;
}

:deep(.branch-selection) {
  height: 32px;
  width: 240px;

  .dropdown-header {
    background: none;
    border: 0;
    margin-left: auto;
    max-width: 100%;
    min-height: auto;
    min-width: 120px;
    padding: 7px 12px;
    width: fit-content;

    &:hover {
      background: none;
    }
  }

  .expand-icon-wrapper {
    display: flex;
    position: absolute;
    right: 4px;
    top: 4px;

    svg {
      fill: map.get(colors.$colors, 'white');
    }
  }

  .dropdown-list {
    max-width: 100%;
    min-width: 120px;
    position: absolute;
    right: 0;
    width: fit-content;
  }

  .selected-value {
    color: map.get(colors.$colors, 'gallery');
  }

  .command-icons {
    top: 5px;
  }

  .dropdown-header-arrow {
    color: map.get(colors.$colors, 'white');
  }
}

.vertical-divider {
  height: 16px;
  margin: 0 16px;
  position: relative;
}

:deep(.dropdown-trigger .menu-icon) {
  background: none;

  .button-icon {
    fill: map.get(colors.$colors, 'white');
  }

  &:hover {
    background-color: map.get(colors.$colors, 'fun-blue');
    fill: map.get(colors.$colors, 'white');
  }

  &:focus:not(:active) {
    background-color: map.get(colors.$colors, 'fun-blue');
    fill: map.get(colors.$colors, 'white');
  }

  &:active {
    background-color: map.get(colors.$colors, 'biscay');
    fill: map.get(colors.$colors, 'white');
  }

  &:disabled {
    fill: map.get(colors.$colors, 'biscay');
  }
}

.dropdown-menu {
  left: auto;
  max-width: $dropdown-max-width;
  min-width: max-content;
  padding-top: 12px;
  right: 0;
}

:deep(.dropdown-menu-content) {
  .account-info {
    background-color: map.get(colors.$colors, 'alabaster');
    max-width: $dropdown-max-width;
    width: 100%;
  }

  .account-title {
    color: map.get(colors.$colors, 'shuttle-gray');
    font-size: 12px;
    padding: 16px 12px 4px;
  }

  .account-details {
    color: map.get(colors.$colors, 'bright-gray');
    display: flex;
    flex-direction: column;
    padding: 11px 12px;
  }

  .account-name {
    font-size: 16px;
    line-height: 20px;
  }

  .account-email {
    font-size: 14px;
    line-height: 18px;
  }
}
</style>
